import React, { useState } from 'react';
import {
  Headphones,
  HelpCircle,
  MessageSquare,
  Clock,
  CheckCircle2,
  AlertCircle,
  ShieldCheck,
  Lock,
  Plus
} from 'lucide-react';
import { INITIAL_SUPPORT_TICKETS } from '../../data/growthData';
import { CustomerSupportTicket } from '../../types';

export const CustomerSuccessCenter: React.FC = () => {
  const [tickets, setTickets] = useState<CustomerSupportTicket[]>(INITIAL_SUPPORT_TICKETS);
  const [showNewModal, setShowNewModal] = useState<boolean>(false);

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-900">
              P12.6 — Customer Success & Support Center
            </span>
            <span className="flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
              <Lock className="w-3.5 h-3.5 text-emerald-600" />
              Private Financial Data Protection Active
            </span>
          </div>
          <h2 className="text-xl font-black text-slate-900 tracking-tight">
            Customer Success Foundation & Self-Service Support Desk
          </h2>
          <p className="text-xs text-slate-600 mt-1 max-w-3xl">
            Self-service help center and support desk. Facilitates calculation inquiries and technical help while strictly forbidding the automatic attachment of private scenario inputs.
          </p>
        </div>

        <button
          onClick={() => setShowNewModal(true)}
          className="px-3.5 py-2 rounded-lg bg-blue-900 text-white text-xs font-bold hover:bg-blue-800 transition-colors flex items-center gap-1.5 cursor-pointer shadow-xs"
        >
          <Plus className="w-4 h-4" />
          <span>New Support Inquiry</span>
        </button>
      </div>

      {/* Support KPI Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
          <span className="text-slate-500 text-[11px] block font-medium">Open Support Tickets</span>
          <span className="text-lg font-black text-slate-900">1 Ticket</span>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
          <span className="text-slate-500 text-[11px] block font-medium">Critical Priority</span>
          <span className="text-lg font-black text-emerald-700">0 Critical</span>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
          <span className="text-slate-500 text-[11px] block font-medium">Avg Resolution Time</span>
          <span className="text-lg font-black text-blue-700">2.4 Hours</span>
        </div>
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
          <span className="text-slate-500 text-[11px] block font-medium">Private Data Attachment</span>
          <span className="text-lg font-black text-emerald-700">0% (Strictly Prohibited)</span>
        </div>
      </div>

      {/* Active Tickets Dashboard */}
      <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-xs space-y-4">
        <div className="flex items-center justify-between pb-3 border-b border-slate-100">
          <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
            <Headphones className="w-4 h-4 text-blue-900" />
            Active Customer Success Support Queue
          </h3>
          <span className="text-xs text-slate-500 font-medium">SLA SLA-S1 Standard</span>
        </div>

        <div className="space-y-3">
          {tickets.map(tkt => (
            <div key={tkt.id} className="p-4 bg-slate-50 rounded-xl border border-slate-200 flex flex-col md:flex-row justify-between md:items-center gap-3 text-xs">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-slate-900">{tkt.ticketNumber}</span>
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-200 text-slate-800">
                    {tkt.category}
                  </span>
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-900">
                    Context: {tkt.toolContext}
                  </span>
                </div>
                <p className="text-slate-700 font-medium">{tkt.messagePreview}</p>
                <div className="flex items-center gap-2 text-[11px] text-slate-500">
                  <span>User: {tkt.userEmailMasked}</span>
                  <span>•</span>
                  <span>Created: {new Date(tkt.createdAt).toLocaleTimeString()}</span>
                </div>
              </div>

              <div className="flex items-center gap-3 shrink-0">
                <span className="px-2.5 py-1 rounded text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-200 flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3 text-emerald-700" />
                  NO Financial Data Attached
                </span>
                <span className={`px-2.5 py-1 rounded text-xs font-bold ${
                  tkt.status === 'IN_PROGRESS' ? 'bg-amber-100 text-amber-900' : 'bg-emerald-100 text-emerald-900'
                }`}>
                  {tkt.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
